/*
 * COPYRIGHT (c) 2023
 * HONEYWELL INC.,
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import {
  Accordion,
  Button,
  Card,
  Checkbox,
  Icon,
  Input,
  Popup
} from '@scuf/common';
import React, { useEffect, useState } from 'react';
import './FilterComponent.scss';
import useFilter from './useFilter';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import { ISummaryTableDataObject } from '../../../DashboardKPIWidgets/IEnergyManagementProps';

interface IFilterComponentProps {
  onApply: (filteredData: ISummaryTableDataObject[]) => void;
  onReset: () => void;
  data: ISummaryTableDataObject[];
}
function FilterComponent(props: IFilterComponentProps) {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const { onReset } = props;
  const [resetAll, setResetAll] = useState<boolean>(true);

  const {
    isOpen,
    data,
    onCheckboxClick,
    hidePopup,
    handleOpen,
    applyOnClick,
    cancelOnClick,
    onSearch,
    onSelectAll
  } = useFilter(props);

  useEffect(() => {
    isOpen &&
      setResetAll(
        data.every((element: ISummaryTableDataObject) => element.isSelected)
      );
  }, [isOpen]);

  useEffect(() => {
    if (data.every((element: ISummaryTableDataObject) => element.isSelected)) {
      setResetAll(true);
    } else {
      setResetAll(false);
    }
  }, [data]);

  const [searchText, setSearchText] = useState('');
  return (
    <Popup
      element={<Icon root="common" name="filter" size="medium" />}
      hoverable={false}
      position="bottom right"
      on="click"
      open={isOpen}
      onOpen={handleOpen}
      onClose={hidePopup}
    >
      <Card className="filter-popup">
        <Card.Header title={'Filters'}>
          <Button
            className="reset-filter-btn"
            type="link"
            content={t(d.RESET_ALL_FILTERS)}
            onClick={() => {
              setSearchText('');
              if (typeof onSearch === 'function') {
                onSearch('');
              }
              onReset();
            }}
          />
        </Card.Header>
        <Card.Content className="popup-content">
          <Accordion>
            <Accordion.Content className="accordion" title="Accounts">
              <div className="content-list">
                {
                  <Input
                    key={1}
                    label={t(d.SEARCH)}
                    placeholder={t(d.SEARCH)}
                    className="search-account"
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e);
                      onSearch(e);
                    }}
                  />
                }
                {!searchText && (
                  <Checkbox
                    key={0}
                    label={t(d.SELECTALL)}
                    checked={resetAll}
                    onChange={(checked) => {
                      setResetAll(!resetAll);
                      onSelectAll(checked);
                    }}
                    disabled={data.length === 1}
                  />
                )}
                {data.map((element: ISummaryTableDataObject) => (
                  <Checkbox
                    key={element.accountNumber}
                    label={element.accountNumber}
                    checked={element.isSelected}
                    onChange={(checked) =>
                      onCheckboxClick(checked, element, 'accountNumber')
                    }
                    disabled={!searchText && data.length === 1}
                  />
                ))}
              </div>
            </Accordion.Content>
          </Accordion>
        </Card.Content>
        <Card.Footer>
          <Button
            type="secondary"
            size="small"
            className="cancel-btn action-btn"
            onClick={() => {
              setSearchText('');
              cancelOnClick();
            }}
            content={t(d.CANCEL)}
          />
          <Button
            disabled={
              !data.filter((item: ISummaryTableDataObject) => item.isSelected)
                .length ||
              (!searchText && data.length === 1)
            }
            type="primary"
            size="small"
            className="apply-btn action-btn"
            onClick={() => {
              setSearchText('');
              applyOnClick();
            }}
            content={t(d.APPLY)}
          />
        </Card.Footer>
      </Card>
    </Popup>
  );
}

export default FilterComponent;
